import { Button, ButtonGroup, Grid } from "@mui/material";
import styled from "styled-components/macro";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { TimeRange } from "types/types";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import { Download } from "@mui/icons-material";
import {
  DATA_DUMP_FRAGMENT,
  DATA_DUMP_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import { CSVLink } from "react-csv";
import { useQuery } from "@apollo/client";
import { medBlue } from "constants/colors";
import Papa from "papaparse/papaparse.min.js";
import { useEffect, useState } from "react";
import { formatHoursMinutes } from "utils/timeUtils";
import { format } from "date-fns";
import { errorOverlayStateAtom } from "reports/components/ErrorOverlay";
import { useAtom } from "jotai";

export function GenerateCSVButtonGroup({
  range,
  fileName,
  hasSubmitted = false,
  disabled = false,
  onClick = () => null,
  machemetryIds,
}: {
  range: TimeRange;
  fileName: string;
  disabled?: boolean;
  hasSubmitted?: boolean;
  onClick?: () => void;
  machemetryIds: string[];
}) {
  const [completed, setCompleted] = useState(false);
  return (
    <GridStyles>
      <ButtonGroup
        variant="contained"
        color="primary"
        className="generateDownloadGroup"
      >
        <Button
          onClick={onClick}
          className="generate"
          disabled={disabled || hasSubmitted}
        >
          {!hasSubmitted
            ? "Generate CSV File"
            : completed
              ? "Done!"
              : "Generating..."}
        </Button>
        {hasSubmitted ? (
          <DataDumpGetterCSVDownloadIconButton
            {...{ range, fileName, machemetryIds, setCompleted }}
          />
        ) : (
          <DisabledDownloadButton />
        )}
      </ButtonGroup>
    </GridStyles>
  );
}
export const getFileName = (ckey, name, range) => {
  return `${ckey}_${range.ge}_${name}_${getDateTimeRange(range)}.csv`;
};
const getDateTimeRange = ({ ge, lt }) => {
  const startDateObj = new Date(parseInt(ge, 10));
  const endDateObj = new Date(parseInt(lt, 10));
  const startDateUnderscore = startDateObj.toDateString().split(" ").join("_");
  const endDateUnderscore = endDateObj.toDateString().split(" ").join("_");
  return `${startDateUnderscore},${startDateObj.toLocaleTimeString()} - ${endDateUnderscore},${endDateObj.toLocaleTimeString()}`;
};
const DownloadButtonStyles = styled(Button)`
  padding: 12px !important;
  &.MuiButton-root {
    min-width: 40px;
  }
`;
const GridStyles = styled(Grid)`
  display: inline;

  .generate {
    color: white;
    border-radius: 4px 0 0 4px;
  }
`;
const DisabledDownloadButton = () => {
  return (
    <DownloadButtonStyles disabled={true}>
      <Download />
    </DownloadButtonStyles>
  );
};
function DataDumpGetterCSVDownloadIconButton({
  range,
  fileName,
  machemetryIds,
  setCompleted,
}: {
  range: TimeRange;
  fileName: string;
  machemetryIds: string[];
  setCompleted: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { dataDump, loading, error } = useFetchDataDump({
    range,
    machemetryIds,
  });
  useEffect(() => {
    if (dataDump && !loading) {
      setCompleted(true);
    }
  }, [dataDump, loading, setCompleted]);
  // show error overlay
  const [, setErrorOverlayState] = useAtom(errorOverlayStateAtom);
  useEffect(() => {
    if (error) {
      setErrorOverlayState({
        error: true,
        message: error.message,
      });
    }
  }, [error, setErrorOverlayState]);
  const dataDumpDefinition = DATA_DUMP_FRAGMENT.definitions[0] as {
    selectionSet: any;
  };
  const dataDumpFieldsArr = dataDumpDefinition.selectionSet.selections.map(
    field => field.name.value
  );

  // Only show button if there is data
  const ready = dataDump && dataDump.length > 0 && !loading && !error;
  if (ready) {
    const dataDumpCleaned = addReadableDatesAndDuration(dataDump);
    const dataDumpUnparsed = Papa.unparse(
      dataDumpCleaned,
      getPapaUnparseConfigs([
        "geReadable",
        "ltReadable",
        "durationMs",
        "durationReadable",
        ...dataDumpFieldsArr,
      ])
    );

    return (
      <CSVLinkStyles>
        <CSVLink filename={fileName} data={dataDumpUnparsed}>
          <Download />
        </CSVLink>
      </CSVLinkStyles>
    );
  }
  return (
    <div
      style={{
        width: "50px",
        height: "50px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <LoadingCircle widthPct={100} marginTopEm={0} />
    </div>
  );
}

function addReadableDatesAndDuration(dataDump) {
  return dataDump.map(dump => {
    const ge = parseInt(dump.ge, 10);
    const lt = parseInt(dump.lt, 10);
    const geReadable = format(ge, "yyyy-MM-dd HH:mm:ss");
    const ltReadable = format(lt, "yyyy-MM-dd HH:mm:ss");
    const durationMs = lt - ge;
    const durationReadable = formatHoursMinutes(durationMs);

    return {
      geReadable,
      ltReadable,
      durationMs,
      durationReadable,
      ...dump,
    };
  });
}
const CSVLinkStyles = styled.div`
  display: inline-flex;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;
  a {
    padding: 12px;
    text-decoration: none;
    border: 1px solid ${medBlue};
    background-color: ${medBlue};
    color: white;
  }
`;
function getPapaUnparseConfigs(columns: string[]) {
  return {
    quotes: false,
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ",",
    header: true,
    newline: "\r\n",
    skipEmptyLines: false,
    columns,
  };
}
/**
 * Query to fetch data dumps for given range and machemetry ids
 *
 * @param {{
 *   range: TimeRangeNN;
 *   machemetryIds?: string[];
 * }} {
 *   range,
 *   machemetryIds,
 * }
 * @return {*}
 */
function useFetchDataDump({
  range,
  machemetryIds,
}: {
  range: TimeRange;
  machemetryIds?: string[];
}) {
  const ckey = useCustomerKey();
  const { loading, error, data } = useQuery(DATA_DUMP_QUERY, {
    client: midgardClients.get(ckey),
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      ge: String(range.ge),
      lt: String(range.lt),
      // If no machemetry Ids, submit without any
      // The query will grab all machemetry ids from the customer json
      ...(machemetryIds && machemetryIds.length > 0 && { machemetryIds }),
    },
  });

  // Join tagPath array with "/" so it doesn't get moved to another column when converting to csv
  const dataDumpFormatted = data?.dataDump?.map(dump => ({
    ...dump,
    tagPath: dump?.tagPath?.join("/"),
  }));

  return {
    dataDump: dataDumpFormatted,
    loading,
    error,
  };
}
